// SesionesPosPage.tsx — Co Marca Admin — estilo AboutusProjects
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Banknote, Eye, FileText, ChevronRight, Home } from 'lucide-react';
import { fetchSesionesPorUsuario } from '../../../services/pos/sesionPOSService';
import { fetchReporteSesionPOS } from '../../../services/pos/sesionPOSService';
import { generarPDFReporteSesionPOS } from '../../../utils/pdfReporteSesionPOS';

const colors = {
    primary:        '#612570',
    primaryDark:    '#4a154b',
    secondary:      '#c1348b',
    secondaryLight: '#059669',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
.ses-page * { font-family:'Plus Jakarta Sans',sans-serif; box-sizing:border-box; }
@keyframes sesFadeUp { from{opacity:0;transform:translateY(16px);} to{opacity:1;transform:translateY(0);} }
@keyframes spin { to{transform:rotate(360deg);} }
.ses-fadeup   { animation:sesFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ses-fadeup-1 { animation:sesFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.ses-table-row { transition:background .12s; }
.ses-table-row:hover { background:#faf5ff; }

.ses-btn-view {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'Plus Jakarta Sans',sans-serif;
    background:#fdf2f8; color:${colors.secondary};
    transition:all .2s;
}
.ses-btn-view:hover { background:${colors.secondary}; color:#fff; transform:translateY(-1px); }

.ses-btn-pdf {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'Plus Jakarta Sans',sans-serif;
    background:#ecfdf5; color:#065f46;
    transition:all .2s;
}
.ses-btn-pdf:hover:not(:disabled) { background:#059669; color:#fff; transform:translateY(-1px); }
.ses-btn-pdf:disabled { opacity:.5; cursor:not-allowed; }

.ses-btn-back {
    display:inline-flex; align-items:center; gap:7px;
    background:#fff; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:9px 16px; font-size:.85rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.ses-btn-back:hover { background:#f8fafc; border-color:${colors.secondary}40; color:${colors.secondary}; }

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97,37,112,.2); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97,37,112,.4); }
`;

const SesionesPosPage: React.FC = () => {
    const { posId } = useParams<{ posId: string }>();
    const navigate = useNavigate();
    const [sesiones, setSesiones] = useState<any[]>([]);
    const [loading, setLoading]   = useState(true);
    const [generandoPDF, setGenerandoPDF] = useState<number|null>(null);

    const handleGenerarPDF = async (sesionId: number) => {
        setGenerandoPDF(sesionId);
        try {
            const data = await fetchReporteSesionPOS(sesionId);
            await generarPDFReporteSesionPOS(data);
        } catch { alert('Error al generar el reporte. Por favor, intente nuevamente.'); }
        finally { setGenerandoPDF(null); }
    };

    useEffect(() => {
        if (!posId) return;
        fetchSesionesPorUsuario(Number(posId))
            .then(setSesiones)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [posId]);

    const formatDate = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString('es-ES', { year:'numeric', month:'short', day:'numeric' });
    };
    const formatTime = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleTimeString('es-ES', { hour:'2-digit', minute:'2-digit' });
    };
    const formatSaldo = (v: any) => {
        const n = Number(v);
        return isNaN(n) ? '0.00' : n.toFixed(2);
    };

    const getEstadoStyle = (estado: string) => {
        switch (estado) {
            case 'cerrada': return { bg:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', dot:colors.secondaryLight };
            case 'abierta': return { bg:`${colors.secondary}12`, border:`1px solid ${colors.secondary}25`, color:colors.secondary, dot:colors.secondary };
            default:        return { bg:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, dot:colors.primary };
        }
    };

    // ── Loading ──
    if (loading) return (
        <>
            <style>{css}</style>
            <div className="ses-page" style={{ background:'#fff', minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ width:'48px', height:'48px', position:'relative', margin:'0 auto 14px' }}>
                        <div style={{ position:'absolute', inset:0, border:`3px solid ${colors.secondary}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
                        <div style={{ position:'absolute', inset:'8px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <Calendar size={14} color="#fff"/>
                        </div>
                    </div>
                    <p style={{ color:'#64748b', fontWeight:'600' }}>Cargando sesiones...</p>
                </div>
            </div>
        </>
    );

    return (
        <>
            <style>{css}</style>
            <div className="ses-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="ses-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/pos-list" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Puntos de Venta
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Sesiones</span>
                    </nav>

                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.4rem,3vw,1.9rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Sesiones del <span style={{ color:colors.primary }}>Punto de Venta</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>
                                {sesiones.length} sesiones registradas en este punto de venta
                            </p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button className="ses-btn-back" onClick={()=>navigate('/pos-list')}>
                            <ArrowLeft size={14}/> Volver
                        </button>
                    </div>
                </div>

                {/* ── TABLA ── */}
                <div className="ses-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    {/* Contador */}
                    <div style={{ padding:'14px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.secondary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Calendar size={15} color={colors.secondary}/>
                            </div>
                            <div>
                                <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{sesiones.length}</span>
                                <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500', marginLeft:'4px' }}>sesiones registradas</span>
                            </div>
                        </div>
                        {sesiones.length > 0 && (
                            <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', color:'#94a3b8' }}>
                                <FileText size={12}/> PDF disponible por sesión
                            </div>
                        )}
                    </div>

                    <div className="custom-scroll" style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                            <thead>
                                <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                    {[
                                        { label:'Apertura',     icon:<Calendar size={11}/> },
                                        { label:'Cierre',       icon:<Clock size={11}/> },
                                        { label:'Usuario',      icon:<User size={11}/> },
                                        { label:'Saldo Inicial',icon:<Banknote size={11}/> },
                                        { label:'Saldo Final',  icon:<Banknote size={11}/> },
                                        { label:'Estado',       icon:null },
                                        { label:'Acciones',     icon:null },
                                    ].map((h,i)=>(
                                        <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.65rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                            <span style={{ display:'inline-flex', alignItems:'center', gap:'5px' }}>
                                                {h.icon && <span style={{ color:colors.secondary }}>{h.icon}</span>}
                                                {h.label}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sesiones.map(ses=>{
                                    const est = getEstadoStyle(ses.estado);
                                    return (
                                        <tr key={ses.id} className="ses-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                            {/* Apertura */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{formatDate(ses.fecha_apertura)}</p>
                                                <p style={{ fontSize:'.72rem', color:'#94a3b8', marginTop:'2px' }}>{formatTime(ses.fecha_apertura)}</p>
                                            </td>
                                            {/* Cierre */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                {ses.fecha_cierre ? (
                                                    <>
                                                        <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{formatDate(ses.fecha_cierre)}</p>
                                                        <p style={{ fontSize:'.72rem', color:'#94a3b8', marginTop:'2px' }}>{formatTime(ses.fecha_cierre)}</p>
                                                    </>
                                                ) : (
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'.72rem', color:colors.secondary, fontWeight:'600', background:`${colors.secondary}10`, padding:'3px 8px', borderRadius:'6px' }}>
                                                        <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondary, display:'inline-block' }}/>
                                                        En curso
                                                    </span>
                                                )}
                                            </td>
                                            {/* Usuario */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                <p style={{ fontSize:'.85rem', color:'#374151', fontWeight:'600' }}>{ses.usuario_nombre}</p>
                                            </td>
                                            {/* Saldo inicial */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                <p style={{ fontSize:'.88rem', fontWeight:'800', color:'#0f172a' }}>
                                                    {formatSaldo(ses.saldo_inicial)} <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Bs.</span>
                                                </p>
                                            </td>
                                            {/* Saldo final */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                {ses.saldo_final ? (
                                                    <p style={{ fontSize:'.88rem', fontWeight:'800', color:'#0f172a' }}>
                                                        {formatSaldo(ses.saldo_final)} <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Bs.</span>
                                                    </p>
                                                ) : (
                                                    <span style={{ fontSize:'.75rem', color:'#cbd5e1', fontStyle:'italic' }}>N/A</span>
                                                )}
                                            </td>
                                            {/* Estado */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:est.bg, border:est.border, color:est.color, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700', textTransform:'capitalize' }}>
                                                    <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:est.dot, display:'inline-block' }}/>
                                                    {ses.estado}
                                                </span>
                                            </td>
                                            {/* Acciones */}
                                            <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                <div style={{ display:'flex', gap:'6px' }}>
                                                    <button className="ses-btn-view" onClick={()=>navigate(`/sesiones-pos/${ses.id}`)}>
                                                        <Eye size={13}/> Detalles
                                                    </button>
                                                    <button className="ses-btn-pdf" disabled={generandoPDF===ses.id} onClick={()=>handleGenerarPDF(ses.id)}>
                                                        {generandoPDF===ses.id
                                                            ? <><span style={{ width:'12px', height:'12px', border:'2px solid rgba(6,95,70,.4)', borderTopColor:'#065f46', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }}/> PDF</>
                                                            : <><FileText size={13}/> PDF</>
                                                        }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty state */}
                    {sesiones.length===0 && (
                        <div style={{ padding:'60px 24px', textAlign:'center' }}>
                            <div style={{ width:'56px', height:'56px', borderRadius:'18px', background:`${colors.secondary}10`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                                <Calendar size={26} color={colors.secondary} style={{ opacity:.4 }}/>
                            </div>
                            <p style={{ fontWeight:'800', fontSize:'1rem', color:'#0f172a', marginBottom:'6px' }}>No se encontraron sesiones</p>
                            <p style={{ color:'#94a3b8', fontSize:'.85rem', marginBottom:'16px' }}>Este punto de venta no tiene sesiones registradas aún.</p>
                            <button className="ses-btn-back" onClick={()=>navigate(-1)}><ArrowLeft size={14}/> Volver atrás</button>
                        </div>
                    )}
                </div>

                {/* ── INFO REPORTES ── */}
                {sesiones.length > 0 && (
                    <div style={{ marginTop:'16px', display:'flex', gap:'10px', background:`${colors.secondary}08`, border:`1.5px solid ${colors.secondary}20`, borderRadius:'14px', padding:'14px 18px' }}>
                        <FileText size={16} color={colors.secondary} style={{ flexShrink:0, marginTop:'2px' }}/>
                        <div>
                            <p style={{ fontSize:'.82rem', color:'#374151', fontWeight:'600', marginBottom:'2px' }}>Reportes disponibles</p>
                            <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.5 }}>
                                Genera reportes PDF individuales para cada sesión. Cada reporte incluye información detallada de citas, pagos y movimientos de caja.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SesionesPosPage;
